import { Link, useLocation } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import { FaShoppingCart, FaUser, FaBars, FaTimes, FaCertificate } from 'react-icons/fa'
import { useCartStore } from '@/store/cartStore'
import { useUserStore } from '@/store/userStore'
import Logo from '@/components/common/Logo'
import CartSidebar from '@/components/shop/CartSidebar'

const Header = () => {
  const { t } = useTranslation()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [cartSidebarOpen, setCartSidebarOpen] = useState(false)
  const location = useLocation()
  const { getTotalItems } = useCartStore()
  const { isAuthenticated } = useUserStore()
  const cartItemsCount = getTotalItems()

  const navigation = [
    { name: t('nav.home'), href: '/' },
    { name: t('nav.shop'), href: '/shop' },
    { name: t('nav.about'), href: '/about' },
    { name: t('nav.contact'), href: '/contact' },
  ]

  const isActive = (path: string) => location.pathname === path

  // Close mobile menu when route changes
  useEffect(() => {
    setMobileMenuOpen(false)
  }, [location.pathname])

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
      className="sticky top-0 z-50 transition-all duration-300 bg-white border-b border-gray-200"
      style={{ backgroundColor: '#FFFFFF' }}
    >

      <div className="relative container-custom px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20 gap-2 sm:gap-4">
          {/* Logo */}
          <Logo />

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1 flex-grow justify-center">
            {navigation.map((item, index) => (
              <Link
                key={item.name}
                to={item.href}
                className="relative group"
              >
                <motion.span
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 * index }}
                  className={`
                    relative px-4 py-2.5 rounded-xl text-sm font-semibold transition-all duration-300
                    ${isActive(item.href)
                      ? 'text-primary-500'
                      : 'text-gray-700 hover:text-black'
                    }
                  `}
                >
                  {item.name}
                </motion.span>

                {/* Active indicator */}
                {isActive(item.href) && (
                  <>
                    <motion.div
                      layoutId="activeNavIndicator"
                      className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1/2 h-0.5 bg-primary-500 rounded-full"
                      initial={false}
                      transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                    />
                    <motion.div
                      layoutId="activeNavBackground"
                      className="absolute inset-0 bg-gray-50 rounded-xl -z-10"
                      initial={false}
                      transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                    />
                  </>
                )}
              </Link>
            ))}
          </nav>


          {/* Right Icons */}
          <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
            {/* DIN Certificate Badge */}
            <motion.a
              href="https://www.dincertco.tuv.com/registrations/60185364?locale=de"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden md:flex items-center gap-2 px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg hover:border-gray-300 transition-colors mr-2 group"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="p-1 bg-white rounded-full border border-gray-100 shadow-sm">
                <FaCertificate className="w-3 h-3 text-gray-600 group-hover:text-primary-500 transition-colors" />
              </div>
              <div className="flex flex-col leading-none">
                <span className="text-[10px] text-gray-500 font-medium uppercase tracking-wider">Zertifiziert</span>
                <span className="text-xs font-bold text-gray-800 group-hover:text-primary-600 transition-colors">DIN Geprüft</span>
              </div>
            </motion.a>

            {/* Cart Icon */}
            <motion.div
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <button
                onClick={() => setCartSidebarOpen(true)}
                className="relative p-2 sm:p-2.5 rounded-lg sm:rounded-xl text-gray-700 hover:text-black transition-all duration-300 group"
              >
                <FaShoppingCart className="w-4 h-4 sm:w-5 sm:h-5 group-hover:scale-110 transition-transform" />
                {cartItemsCount > 0 && (
                  <motion.span
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="absolute -top-1 -right-1 bg-gradient-to-r from-primary-500 via-primary-600 to-primary-700 text-white text-xs font-black rounded-full min-w-[22px] h-5 flex items-center justify-center px-1.5 shadow-lg shadow-primary-500/50 ring-2 ring-dark-900"
                  >
                    {cartItemsCount > 9 ? '9+' : cartItemsCount}
                  </motion.span>
                )}
                {/* Pulse animation when cart has items */}
                {cartItemsCount > 0 && (
                  <motion.span
                    className="absolute -top-1 -right-1 w-5 h-5 bg-primary-500 rounded-full opacity-75"
                    animate={{
                      scale: [1, 1.4, 1],
                      opacity: [0.75, 0, 0.75],
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: 'easeInOut',
                    }}
                  />
                )}
              </button>
            </motion.div>

            {/* User Icon */}
            <motion.div
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <Link
                to={isAuthenticated ? '/account' : '/account'}
                className="p-2.5 rounded-xl text-gray-700 hover:text-black transition-all duration-300"
              >
                <FaUser className="w-4 h-4 sm:w-5 sm:h-5" />
              </Link>
            </motion.div>

            {/* Mobile menu button */}
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 sm:p-2.5 rounded-lg sm:rounded-xl text-gray-700 hover:text-black transition-all duration-300"
              aria-label="Toggle menu"
            >
              <AnimatePresence mode="wait">
                {mobileMenuOpen ? (
                  <motion.div
                    key="close"
                    initial={{ rotate: -90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: 90, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <FaTimes className="w-4 h-4 sm:w-5 sm:h-5" />
                  </motion.div>
                ) : (
                  <motion.div
                    key="menu"
                    initial={{ rotate: 90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: -90, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <FaBars className="w-4 h-4 sm:w-5 sm:h-5" />
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <>
              {/* Backdrop */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setMobileMenuOpen(false)}
                className="fixed inset-0 top-20 bg-black/20 backdrop-blur-sm lg:hidden"
              />

              {/* Menu */}
              <motion.div
                initial={{ opacity: 0, height: 0, y: -20 }}
                animate={{ opacity: 1, height: 'auto', y: 0 }}
                exit={{ opacity: 0, height: 0, y: -20 }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
                className="lg:hidden overflow-hidden border-t border-gray-200 bg-white"
              >
                <nav className="flex flex-col py-2">
                  {navigation.map((item, index) => (
                    <motion.div
                      key={item.name}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <Link
                        to={item.href}
                        onClick={() => setMobileMenuOpen(false)}
                        className={`
                          relative block px-6 py-3.5 text-base font-semibold transition-all duration-300
                          ${isActive(item.href)
                            ? 'text-primary-500 bg-gray-50 border-r-4 border-primary-500'
                            : 'text-gray-700 hover:text-black'
                          }
                        `}
                      >
                        {item.name}
                        {isActive(item.href) && (
                          <motion.div
                            layoutId="mobileActiveNav"
                            className="absolute left-0 top-0 bottom-0 w-1 bg-primary-500 rounded-r-full"
                            initial={false}
                            transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                          />
                        )}
                      </Link>
                    </motion.div>
                  ))}
                </nav>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>

      {/* Cart Sidebar */}
      <CartSidebar isOpen={cartSidebarOpen} onClose={() => setCartSidebarOpen(false)} />
    </motion.header>
  )
}

export default Header
